import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { businessProfileService } from '../services/businessProfileService';
import { tpayService } from '../services/tpayService';
import { facilityService } from '../services/facilityService';
import { getEmptyFormData } from '../utils/businessProfileValidation';
import { copyBookingLink } from '../utils/deepLinks';
import type { BusinessProfileFormData, LegalForm, TPayCategory, BusinessProfileDetail } from '../types/businessProfile';
import type { Facility } from '../types/facility';
import { ApiError } from '../services/apiClient';

// SW-99: shared profile/facilities/dictionaries data layer, extracted from
// the former single BusinessProfilePage so both OperationsPage (Operacje)
// and ConfigurationPage (Konfiguracja) can independently load what they
// need.
export function useBusinessProfileData() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState<BusinessProfileFormData | null>(null);
  const [fullProfile, setFullProfile] = useState<BusinessProfileDetail | null>(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [legalForms, setLegalForms] = useState<LegalForm[]>([]);
  const [categories, setCategories] = useState<TPayCategory[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadFacilities = useCallback(async () => {
    if (!token) return;
    try {
      const data = await facilityService.getMyFacilities(token);
      setFacilities(data);
    } catch {
      // Silently fail - facilities are optional
    }
  }, [token]);

  const loadData = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setLoadError(null);

    try {
      const [legalFormsData, categoriesData] = await Promise.all([
        tpayService.getLegalForms(token),
        tpayService.getCategories(token),
      ]);
      setLegalForms(legalFormsData);
      setCategories(categoriesData);

      try {
        const existingProfile = await businessProfileService.getProfile(token);
        setProfile(mapProfileToFormData(existingProfile));
        setFullProfile(existingProfile);
        setIsNewProfile(false);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setProfile(getEmptyFormData());
          setFullProfile(null);
          setIsNewProfile(true);
        } else {
          throw err;
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setLoadError(message);
      showToast('error', t('common.error'), message);
    } finally {
      setIsLoading(false);
    }
  }, [token, showToast, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (profile?.id) {
      loadFacilities();
    }
  }, [profile?.id, loadFacilities]);

  const handleSave = async (formData: BusinessProfileFormData, facilityPlan?: File) => {
    if (!token || !profile) return;

    setIsSaving(true);

    try {
      if (isNewProfile) {
        await businessProfileService.createProfile(token, formData, facilityPlan);
        showToast('success', t('businessProfile.messages.createSuccess'));
      } else {
        await businessProfileService.updateProfile(token, profile.id!, formData, facilityPlan);
        showToast('success', t('businessProfile.messages.updateSuccess'));
      }

      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save profile';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyBookingLink = async () => {
    if (!profile?.id) return;
    const ok = await copyBookingLink(profile.id);
    if (ok) {
      showToast('success', t('common.linkCopied'));
    } else {
      showToast('error', t('common.linkCopyError'));
    }
  };

  const handleAvatarUpload = async (file: File) => {
    if (!token || !profile?.id) return;

    try {
      const result = await businessProfileService.uploadAvatar(token, profile.id, file);
      setProfile(prev => prev ? { ...prev, avatarUrl: result.avatarUrl } : null);
      showToast('success', t('businessProfile.messages.avatarSuccess'));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload avatar';
      showToast('error', t('common.error'), message);
    }
  };

  return {
    isLoading,
    isSaving,
    profile,
    fullProfile,
    isNewProfile,
    legalForms,
    categories,
    facilities,
    loadError,
    loadData,
    handleSave,
    handleAvatarUpload,
    handleCopyBookingLink,
  };
}

function mapProfileToFormData(profile: BusinessProfileDetail): BusinessProfileFormData {
  return {
    id: profile.id,
    nip: profile.nip || '',
    companyName: profile.companyName || '',
    displayName: profile.displayName || '',
    address: profile.address || '',
    city: profile.city || '',
    postalCode: profile.postalCode || '',
    email: profile.email || '',
    phoneNumber: profile.phoneNumber || '',
    phoneCountry: profile.phoneCountry || 'PL',
    regon: profile.regon || '',
    krs: profile.krs || '',
    legalForm: profile.legalForm || 0,
    categoryId: profile.categoryId || 0,
    website: profile.website || '',
    websiteDescription: profile.websiteDescription || '',
    contactPersonName: profile.contactPersonName || '',
    contactPersonSurname: profile.contactPersonSurname || '',
    latitude: profile.latitude,
    longitude: profile.longitude,
    avatarUrl: profile.avatarUrl,
    facilityPlanUrl: profile.facilityPlanUrl,
    facilityPlanFileName: profile.facilityPlanFileName,
    termsAndConditionsUrl: profile.termsAndConditionsUrl || '',
    autoConfirmTrainerAssociations: profile.autoConfirmTrainerAssociations || false,
    weekdaysSchedule: profile.weekdaysSchedule || [],
    saturdaySchedule: profile.saturdaySchedule || [],
    sundaySchedule: profile.sundaySchedule || [],
    dateSpecificAvailability: profile.dateSpecificAvailability || {},
  };
}
